/**
 * Service for querying data collection actor tasks from prod.data_collection_actor_tasks
 * This is the source of truth for what tasks exist for each tenant
 *
 * Uses SECURITY DEFINER RPC functions to bypass RLS without exposing service role key
 */

import { supabase } from '@/lib/supabase/client';
import { DataCollectionTask } from './dataCollection';

/**
 * Service for managing actor tasks from prod.data_collection_actor_tasks
 * All methods use SECURITY DEFINER RPC functions - requires super_admin role
 */
export const actorTasksAPI = {
  /**
   * Fetch actor tasks for a specific tenant
   * Uses get_actor_tasks_for_tenant RPC function
   * @param tenantId - Tenant ID to filter tasks
   * @param includeDisabled - Include disabled tasks (default: true)
   */
  async getTasksForTenant(
    tenantId: string,
    includeDisabled: boolean = true
  ): Promise<DataCollectionTask[]> {
    try {
      const { data, error } = await supabase.rpc('get_actor_tasks_for_tenant', {
        p_tenant_id: tenantId,
        p_include_disabled: includeDisabled
      });

      if (error) {
        console.error('[actorTasksAPI] Error fetching tasks for tenant:', error);
        throw error;
      }

      // Map to DataCollectionTask interface
      return (data || []).map((task: any) => ({
        name: task.name,
        task_id: task.task_id,
        datasource: task.datasource,
        oem: task.oem,
        state: task.state,
        zone: task.zone,
        zone_slug: task.zone_slug,
        enabled: task.enabled
      }));
    } catch (error) {
      // Re-throw so React Query can handle it properly
      throw error;
    }
  },

  /**
   * Get all tasks across all tenants (for admin purposes)
   * Uses get_all_actor_tasks RPC function
   */
  async getAllTasks(includeDisabled: boolean = true): Promise<DataCollectionTask[]> {
    try {
      const { data, error } = await supabase.rpc('get_all_actor_tasks', {
        p_include_disabled: includeDisabled
      });

      if (error) {
        console.error('[actorTasksAPI] Error fetching all tasks:', error);
        throw error;
      }

      return (data || []).map((task: any) => ({
        name: task.name,
        task_id: task.task_id,
        datasource: task.datasource,
        oem: task.oem,
        state: task.state,
        zone: task.zone,
        zone_slug: task.zone_slug,
        enabled: task.enabled
      }));
    } catch (error) {
      console.error('[actorTasksAPI] Error in getAllTasks:', error);
      return [];
    }
  }
};
